<script lang="ts">
    import { windowReady } from "html-ready";
    import { fly, slide } from "svelte/transition";
    import { modalContent, tips } from "../../stores/globalState";
    import { summoned } from "../../stores/rooster";
    import Key from "../elements/Key.svelte";
    import Action from "../elements/settings/Buttons/Action.svelte";
    import TutorialElement from "./components/TutorialElement.svelte";
    import TutorialStep from "./components/TutorialStep.svelte";
    import { altLabel, isMacintosh } from "../../costants";

    let step = 0;
    let roosterInvokeKey = isMacintosh ? altLabel : 'ctrl';
    $: handleStepChange(step);

    function handleStepChange(step: number) {
        if (step === 2) {
            document.getElementById("rooster-command")?.focus();
        } else if (step === 7) {
            tips.set([{ shortcut: "这里", name: "看我！🙌" }]);
        } else {
            tips.set(null);
        }
    }
</script>

{#await windowReady then _}
    <div class="flex flex-col justify-between w-full h-full font-ubuntu">
        <div class="h-full md:w-4/5 m-auto">
            {#if step === 0}
                <TutorialStep>
                    <div><img class="-mt-10 mb-20 w-2/5 m-auto" src="/media/relaxing-clock-logo-transparent-smile.svg" alt="logo" /></div>
                    <TutorialElement customClass="text-center" index="1">
                        <h2 class="text-5xl font-title">嗨，欢迎来到Relaxing Clock</h2>
                    </TutorialElement>
                </TutorialStep>
            {:else if step === 1}
                <TutorialStep>
                    <div class="text-center">
                        <h2 class="text-5xl font-title">介绍与命令框</h2>
                        <TutorialElement customClass="mt-10 text-3xl" index="1">一种与应用交互的新方式</TutorialElement>
                        <TutorialElement customClass="mt-6 text-2xl" index="2">
                            <Key key={roosterInvokeKey} /><Key key="space" />
                        </TutorialElement>
                        <TutorialElement customClass="mt-6" index="3">
                            {#if $summoned}
                                <h3 class="text-green-400 text-3xl font-title">干得漂亮！👍</h3>
                            {:else}
                                <h3 class="text-3xl font-title">现在试试！</h3>
                            {/if}
                        </TutorialElement>
                    </div>
                </TutorialStep>
            {:else if step === 2}
                <TutorialStep>
                    <div class="text-center">
                        <h2 class="text-5xl font-title">用它来控制Relaxing Clock</h2>
                        <TutorialElement customClass="mt-10 text-3xl" index="1">
                            命令框可以做很多事情！举个例子？无需离开键盘就能设置和管理提醒！
                        </TutorialElement>
                        <TutorialElement customClass="mt-10 text-xl" index="2">
                            输入 "<span class="text-blue-300">reminder:</span> create 玩游戏"<br />
                            或者 "<span class="text-yellow-600">alarm:</span> set 16:20"<br />
                            还不够？"<span class="text-red-300">pin:</span> 就待在那里！"<br /><br />
                            <em>稍后详细介绍，继续阅读</em>
                        </TutorialElement>
                    </div>
                </TutorialStep>
            {:else if step === 3}
                <TutorialStep>
                    <div class="text-center">
                        <h2 class="text-5xl font-title">选择你的配乐 🎧！<br/>直接来自 Spotify！</h2>
                        <TutorialElement customClass="mt-10 text-3xl" index="1">
                            你是高级用户吗？<span class="text-spotify">Spotify</span> 集成让你在工作或浏览网页时选择你最喜欢的专辑或播放列表
                        </TutorialElement>
                        <TutorialElement customClass="mt-10 text-xl" index="2">
                            探索<em>"为你推荐的播放列表"</em>列表，或使用命令框搜索你喜欢的那套 lofi 合集
                        </TutorialElement>
                    </div>
                </TutorialStep>
            {:else if step === 4}
                <TutorialStep>
                    <div class="text-center">
                        <h2 class="text-5xl font-title">需要提高效率？<br/><span class="text-red-300">番茄钟</span>计时器来帮忙！</h2>
                        <TutorialElement customClass="mt-10 text-3xl" index="1">
                            <span class="text-7xl">
                                <i id="pomodoro-icon"
                                    ><span class="icon-tomato-22"><span class="path1" /><span class="path2" /><span class="path3" /><span class="path4" /><span class="path5" /></span></i
                                ></span
                            ><br />
                            用番茄钟周期高效地分配你的时间。
                        </TutorialElement>
                    </div>
                </TutorialStep>
            {:else if step === 5}
                <TutorialStep>
                    <div class="text-center">
                        <h2 class="text-5xl font-title">需要快速记录稍后处理的事情？<br/>只需设置<span class="text-blue-300">提醒</span>！</h2>
                        <TutorialElement customClass="mt-10 text-2xl" index="1">
                            此外，<strong>重复提醒</strong>不会在第一次就过期，它们会持续发送通知直到你手动关闭：所以如果你现在太忙没空处理，也不用担心忘记那个重要的电话！
                        </TutorialElement>
                    </div>
                </TutorialStep>
            {:else if step === 6}
                <TutorialStep>
                    <div class="text-center">
                        <h2 class="text-5xl font-title">用键盘快捷键做更多事！🤓</h2>
                        <TutorialElement customClass="mt-10 text-3xl" index="1">
                            快捷键散布在各处，这样你可以快速调用所有重要功能。<br />
                            例如：
                        </TutorialElement>
                        <TutorialElement customClass="mt-10 text-xl" index="2">
                            <strong>{altLabel} + R</strong> 打开命令框设置提醒 <br />
                            <strong>{altLabel} + Shift + P</strong> 创建新的固定消息 <br /><br />
                            <em><strong>Spotify：</strong></em><br />
                            <strong>{altLabel} + M</strong> 在 Spotify 上搜索播放列表
                        </TutorialElement>
                    </div>
                </TutorialStep>
            {:else if step === 7}
                <TutorialStep>
                    <div class="text-center">
                        <h2 class="text-5xl font-title">错过了什么？🤔</h2>
                        <TutorialElement customClass="mt-10 text-3xl" index="1">
                            <Key key="?" /><br />
                            你可以随时按 '?' 打开帮助页面<br /><br />
                        </TutorialElement>
                    </div>
                </TutorialStep>
            {:else if step === 8}
                <TutorialStep>
                    <div class="text-center">
                        <h2 class="text-5xl font-title">今天就到这里 🤗</h2>
                        <h2 class="text-7xl font-title">享受Relaxing Clock！</h2>
                    </div>
                </TutorialStep>
            {/if}
        </div>
        <footer class="flex flex-col justify-center justify-self-end bg-primary z-10">
            <div class="flex justify-center mb-3">
                {#each Array(9) as _, index}
                    <i class="mx-1 fas fa-circle {step === index ? 'text-primary' : 'text-secondary'} text-xxs cursor-pointer" on:click={() => (step = index)} />
                {/each}
            </div>
            <div class="flex flex-row justify-center justify-self-end">
                <Action label="跳过" custom customClass="border-2 border-primary bg-transparent mx-8" on:click={() => modalContent.set(null)} />
                <Action label="返回" customClass="border-2 border-transparent bg-transparent mx-2 {step > 0 ? '' : 'cursor-not-allowed'}" custom on:click={() => (step === 0 ? 0 : step--)} />
                <Action label={step < 8 ? "下一页" : "关闭"} on:click={() => (step < 8 ? step++ : modalContent.set(null))} />
            </div>
        </footer>
    </div>
{/await}
